/**
 * One point on an OpenStreetMap map — where a driver event was recorded.
 *
 * Deliberately Leaflet + OSM tiles, the portal route card's stack
 * (Pages/Portal/Components/PortalRouteMap.jsx), rather than the Mapbox one
 * the pricing-geo editors use: this is a read-only pin in a log, it needs no
 * routing, and Mapbox needs a token that a staff browser may not have loaded
 * — an event map that silently renders a grey box is worse than none.
 *
 * Lifecycle follows PortalRouteMap: the map is built imperatively in an
 * effect, deferred by a timer so React strict mode's mount → unmount → mount
 * builds one map, and removed on cleanup. It is only mounted while its row is
 * expanded, so collapsing the row tears the map down.
 *
 * Unlike the portal's card this one is on a desktop screen inside a modal, so
 * drag and the zoom buttons are on — staff do want to look around the pin.
 * The scroll wheel is not: it belongs to the modal's scrollbar.
 *
 * If Leaflet cannot start (no tiles, a throw), the point is still readable as
 * a link to openstreetmap.org — never an empty box.
 */
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { osmUrl } from '../Utils/eventPosition';

const OSM_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ZOOM = 16;

const MAP_OPTIONS = {
  scrollWheelZoom: false, // the modal scrolls; the wheel is not ours to take
  zoomControl: true,
  attributionControl: true,
};

// A CSS pin, so no marker image has to survive the bundler.
const PIN_SIZE = 18;
const pinIcon = () => L.divIcon({
  className: 'event-point-map__pin',
  iconSize: [PIN_SIZE, PIN_SIZE],
  iconAnchor: [PIN_SIZE / 2, PIN_SIZE / 2],
});

function EventPointMap({ point, label = 'Event location' }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const { lat, lon } = point;

  useEffect(() => {
    setFailed(false);

    const removeMap = () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };

    const timer = setTimeout(() => {
      try {
        const map = L.map(containerRef.current, MAP_OPTIONS);
        mapRef.current = map;
        map.setView([lat, lon], ZOOM);
        L.tileLayer(OSM_TILES, { maxZoom: 19, attribution: OSM_ATTRIBUTION }).addTo(map);
        L.marker([lat, lon], { icon: pinIcon(), interactive: false, keyboard: false }).addTo(map);
      } catch {
        removeMap();
        setFailed(true);
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      removeMap();
    };
  }, [lat, lon]);

  const href = osmUrl(point, ZOOM);

  return (
    <div className="event-point-map mb-2" data-testid="event-point-map">
      {failed ? (
        <p className="small text-muted mb-1" data-testid="event-point-map-failed">
          <a href={href} target="_blank" rel="noreferrer">{`Open ${lat}, ${lon} on OpenStreetMap`}</a>
        </p>
      ) : (
        <div
          ref={containerRef}
          className="event-point-map__canvas border rounded"
          style={{ height: 180 }}
          role="img"
          aria-label={`${label} — map of ${lat}, ${lon}`}
          data-testid="event-point-map-canvas"
        />
      )}
    </div>
  );
}

export default EventPointMap;
